import { Fragment } from 'react';

import { ProductCarousel } from 'components/Carousel';
import ComponentPlaceholder from 'components/ComponentPlaceholder';
import { LayoutContainer } from 'components/Layout';
import Link, { AnchorLink } from 'components/Link';
import { MicroContentMedia } from 'components/MicroContent';
import ProductListComponent from 'components/ProductList';
import StoreListItem from 'components/StoreListItem';
import Text from 'components/Text';
import { SEARCH_QUERY_PARAM } from 'constants/queryParams';
import { withRequiredProps } from 'hoc';
import { useGlobalLinks, useIsEditing, useSafeFieldValues } from 'hooks';
import { useSearchParams } from 'hooks/navigation';
import type { CategoryCardResponse, Store } from 'models/api';
import type { ProductCard as ProductCardType } from 'models/productCard';
import type {
	Field,
	InspirationPage,
	SitecoreBlockProps,
	SitecoreProductList,
} from 'models/sitecore';
import { getEditorMargin } from 'utils/business-logic';
import { filterTruthy } from 'utils/collection';
import { getAsset, is } from 'utils/helpers';
import { useI18n } from 'utils/i18n';
import { createUrl } from 'utils/url';

const SEARCH_RESULTS_PAGE_INSPIRATION_PAGES_ANCHOR = 'inspiration-pages';

interface Props extends SitecoreBlockProps {
	fields: SitecoreProductList & {
		categories: CategoryCardResponse[];
		inspirationHeading?: Field<string>;
		inspirationPages?: InspirationPage[];
		noQueryText?: Field<string>;
		noSearchResultsText?: Field<string>;
		relatedProducts: ProductCardType[];
		relatedProductsHeading?: Field<string>;
		resultDescriptionText?: Field<string>;
		searchQueryText?: Field<string>;
		searchResultsSpellingSuggestionText?: Field<string>;
		spellingSuggestions?: string[];
		stores?: Store[];
	};
}

function SearchResultsPage({ fields, params }: Props) {
	const texts = useSafeFieldValues(fields);
	const { search } = useGlobalLinks();
	const { tPlural } = useI18n();

	const searchParams = useSearchParams();
	const query = searchParams.get(SEARCH_QUERY_PARAM);
	const {
		categories,
		relatedProducts,
		spellingSuggestions,
		stores,
		total: totalProductCount,
	} = fields;

	const isEditing = useIsEditing();
	const inspirationPages = filterTruthy(
		fields.inspirationPages?.map((item) => ({
			href: item.url,
			imgSrc: getAsset('Templated 2:1', item.fields?.landscapeImage)?.src,
			key: item.id,
			text: item.fields?.navigationTitle?.value,
		})),
		'href',
		'imgSrc',
		'text',
	);

	const hasInspirationPages = is.arrayWithLength(inspirationPages);
	const hasStores = is.arrayWithLength(stores);
	const isEmptySearch =
		totalProductCount === 0 && !hasInspirationPages && !hasStores;

	if (isEditing && isEmptySearch) {
		return (
			<ComponentPlaceholder
				componentName="ProductList"
				className={getEditorMargin(params?.margin, 'Half')}
			/>
		);
	}

	if (!query) {
		return (
			<div className="mx-4 my-20 text-center">
				<Text as="h1" text={texts.noQueryText} />
			</div>
		);
	}

	return (
		<LayoutContainer
			id={params?.anchor}
			outerClassName={getEditorMargin(params?.margin, 'Half')}
		>
			<Text as="h1">
				<>
					{texts.searchQueryText}{' '}
					<span className="[quotes:auto] before:content-[open-quote] after:content-[close-quote]">
						{query}
					</span>
				</>
			</Text>
			{search && is.arrayWithLength(spellingSuggestions) && (
				<Text as="p" className="mt-4">
					{texts.searchResultsSpellingSuggestionText}{' '}
					{spellingSuggestions.map((suggestion, index) => (
						<Fragment key={suggestion}>
							<Link
								href={createUrl(search, { [SEARCH_QUERY_PARAM]: suggestion })}
								underline
							>
								{suggestion}
							</Link>
							{index < spellingSuggestions.length - 1 ? ', ' : ''}
						</Fragment>
					))}
					?
				</Text>
			)}
			{isEmptySearch && (
				<Text as="p" className="mt-6" text={texts.noSearchResultsText} />
			)}
			{!isEmptySearch && (
				<>
					{hasStores && (
						<ul className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4 md:gap-6">
							{stores.map((store) => (
								<StoreListItem
									className="col-span-1"
									key={store.id}
									name={store.name}
									url={store.url}
									openHours={store.todaysOpeningHours?.description ?? ''}
									isOpen={
										store.todaysOpeningHours?.state?.toUpperCase() === 'OPEN'
									}
									address={`${store.streetAddress}, ${store.postalCode}, ${store.city}`}
									storeArea={store.storeArea ?? ''}
								/>
							))}
						</ul>
					)}
					{hasInspirationPages && (
						<p className="mt-6">
							{texts.resultDescriptionText}{' '}
							<AnchorLink
								underline
								anchor={SEARCH_RESULTS_PAGE_INSPIRATION_PAGES_ANCHOR}
							>
								{tPlural(
									'inspiration_item_count_text',
									inspirationPages.length,
								)}
							</AnchorLink>
						</p>
					)}

					<ProductListComponent
						key={query}
						className="mt-r-xl"
						fields={fields}
						gtmItemListId="search"
						gtmItemListName="Search result"
						placeholderComponentName="SearchResultsPage"
						searchQuery={query}
						categories={categories}
					/>

					{is.arrayWithLength(relatedProducts) && (
						<>
							<Text
								as="h2"
								className="mt-r-3xl mb-6"
								text={texts.relatedProductsHeading}
							/>
							<ProductCarousel
								products={fields.relatedProducts}
								gtmItemListId="block"
								gtmItemListName="Search result: Related products"
							/>
						</>
					)}
					{hasInspirationPages && (
						<>
							<Text
								as="h2"
								className="mt-r-3xl mb-4"
								text={texts.inspirationHeading}
								id={SEARCH_RESULTS_PAGE_INSPIRATION_PAGES_ANCHOR}
							/>
							<ul className="grid grid-cols-1 items-start gap-4 sm:grid-cols-2 md:grid-cols-4 md:gap-6">
								{inspirationPages.map((page) => (
									<li key={page.key} className="col-span-1">
										<MicroContentMedia
											href={page.href}
											imgSrc={page.imgSrc}
											imgWidth={393}
											imgHeight={197}
											text={page.text}
										/>
									</li>
								))}
							</ul>
						</>
					)}
				</>
			)}
		</LayoutContainer>
	);
}
SearchResultsPage.displayName = 'SearchResultsPage';

export default withRequiredProps(SearchResultsPage, 'fields');
